<script lang="ts">
import {
  defineComponent,
  reactive,
  PropType,
  ref,
  computed,
} from 'vue';
import { useReadOnlyMode } from 'vue-media-annotator/provides';
import { clientSettings } from 'dive-common/store/settings';
import TypeManagementDialog from 'vue-media-annotator/components/Types/TypeManagementDialog.vue';
import CategoryImportDialog from './CategoryImportDialog.vue';

export default defineComponent({
  name: 'TypeSettingsPanel',
  components: { CategoryImportDialog, TypeManagementDialog },

  props: {
    allTypes: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
    hierarchyActive: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const readOnlyMode = useReadOnlyMode();
    const itemHeight = 45; // in pixels
    const help = reactive({
      import: 'Import multiple Types',
      showEmptyTypes: 'View types that are not used currently.',
      lockTypes: 'Only allows the use of defined types.',
      preventCascadeTypes: 'When a track has multiple types, this will prevent the type from displaying if the max type is not visible in the type list',
      filterTypesByFrame: 'Filters the type list by only types that are visible in the current frame',
      showTotalCount: 'Show each type\'s count for the whole dataset. Rows read frame count / total count.',
      showFrameCount: 'Show each type\'s count on the current frame. Rows read frame count / total count.',
      maxCountButton: 'Show a max count button that will jump to the frame with the max count for the type',
      suppressionType: 'Detections lying at least the Suppression Overlap percent under an annotation of this type are hidden and excluded from counts. A detection carrying an attribute of this name set to true stays visible with its real type (optional dashed/fill styling and an eye-off tag) and is excluded from its own type\'s counts. Leave empty to disable.',
      suppressionThreshold: 'Minimum percent of a detection that must lie under suppression regions for it to be hidden (default 99).',
    });
    const importDialog = ref(false);
    const manageDialog = ref(false);
    const active = ref(false);
    // Always offer the default suppression type even when no annotations use it yet.
    const suppressionTypeItems = computed(() => (
      props.allTypes.includes('Suppressed')
        ? props.allTypes
        : ['Suppressed', ...props.allTypes]
    ));

    return {
      active,
      clientSettings,
      itemHeight,
      help,
      importDialog,
      manageDialog,
      readOnlyMode,
      suppressionTypeItems,
    };
  },
});
</script>

<template>
  <div>
    <v-menu
      v-model="active"
      :nudge-bottom="28"
      :close-on-content-click="false"
      min-width="380"
      max-width="calc(100vw - 24px)"
    >
      <template #activator="{ on, attrs }">
        <v-btn
          icon
          small
          class="mx-0"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon
            small
            :color="active ? 'accent' : 'default'"
          >
            mdi-cog
          </v-icon>
        </v-btn>
      </template>

      <v-card
        outlined
        class="pa-2 pr-4"
        color="blue-grey darken-3"
      >
        <v-card-title>
          Type Settings
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col class="d-flex align-center">
              <v-btn
                small
                outlined
                class="mr-2"
                :title="help.import"
                :disabled="readOnlyMode"
                @click="active = false; importDialog = true"
              >
                <v-icon small class="mr-1">
                  mdi-plus
                </v-icon>
                Add Types
              </v-btn>
              <v-btn
                small
                outlined
                @click="active = false; manageDialog = true"
              >
                <v-icon small class="mr-1">
                  mdi-file-tree
                </v-icon>
                Manage Types
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.showEmptyTypes"
                class="my-0 ml-1 pt-0"
                dense
                label="Show Empty"
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
                max-width="200"
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.showEmptyTypes }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.lockTypes"
                label="Lock Types"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.lockTypes }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.preventCascadeTypes"
                label="Prevent Cascade Types"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
                :disabled="hierarchyActive"
              />
              <div
                v-if="hierarchyActive"
                class="ml-1 text-caption"
              >
                Not applicable to hierarchical types; DIVE selects the deepest qualifying type.
              </div>
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.preventCascadeTypes }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.filterTypesByFrame"
                label="Filter Types by Frame"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.filterTypesByFrame }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.showTotalCount"
                label="Show Total Count"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
                max-width="200"
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.showTotalCount }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.showFrameCount"
                label="Show Frame Count"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
                max-width="200"
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.showFrameCount }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1">
              <v-switch
                v-model="clientSettings.typeSettings.maxCountButton"
                label="Show Max Count Button"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.maxCountButton }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row class="mt-5">
            <v-col class="py-1">
              <v-select
                v-model="clientSettings.typeSettings.suppressionType"
                :items="suppressionTypeItems"
                label="Suppression Region Type"
                class="my-0 ml-1 pt-0"
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.suppressionType }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row
            v-if="clientSettings.typeSettings.suppressionType"
            class="mt-8"
          >
            <v-col class="py-1">
              <v-text-field
                v-model.number="clientSettings.typeSettings.suppressionThreshold"
                label="Suppression Overlap (%)"
                type="number"
                min="1"
                max="100"
                step="1"
                class="my-0 ml-1 pt-0"
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="py-1"
              align="right"
            >
              <v-tooltip
                open-delay="200"
                bottom
              >
                <template #activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help
                  </v-icon>
                </template>
                <span>{{ help.suppressionThreshold }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-menu>

    <v-dialog v-model="manageDialog" max-width="1000" scrollable>
      <TypeManagementDialog v-if="manageDialog" @close="manageDialog = false" />
    </v-dialog>
    <v-dialog
      v-model="importDialog"
      width="550"
    >
      <CategoryImportDialog v-if="importDialog" @close="importDialog = false" />
    </v-dialog>
  </div>
</template>
